import {
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  getLaunchable,
  getLaunchableLifecycleScript,
  type LaunchableVisibility,
  type LaunchableRequestError,
} from "./api";

export const launchableKeys = {
  all: ["launchables"] as const,
  details: () => [...launchableKeys.all, "detail"] as const,
  detail: (launchableId: string, visibility: LaunchableVisibility) =>
    [...launchableKeys.details(), visibility, launchableId] as const,
  lifecycleScript: (launchableId: string, lifecycleScriptId: string) =>
    [
      ...launchableKeys.all,
      "lifecycle-script",
      launchableId,
      lifecycleScriptId,
    ] as const,
};

export function launchableQueryOptions(
  launchableId: string,
  visibility: LaunchableVisibility,
) {
  return {
    queryKey: launchableKeys.detail(launchableId, visibility),
    queryFn: () => getLaunchable(launchableId, visibility),
    enabled: Boolean(launchableId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  };
}

export function useLaunchableQuery(
  launchableId: string,
  visibility: LaunchableVisibility,
) {
  return useQuery<
    Awaited<ReturnType<typeof getLaunchable>>,
    LaunchableRequestError
  >(launchableQueryOptions(launchableId, visibility));
}

type LaunchableLifecycleScriptQueryKey = ReturnType<
  typeof launchableKeys.lifecycleScript
>;

export function launchableLifecycleScriptQueryOptions(
  launchableId: string,
  lifecycleScriptId: string,
): UseQueryOptions<string, Error, string, LaunchableLifecycleScriptQueryKey> {
  return {
    queryKey: launchableKeys.lifecycleScript(launchableId, lifecycleScriptId),
    queryFn: ({
      signal,
    }: QueryFunctionContext<LaunchableLifecycleScriptQueryKey>) =>
      getLaunchableLifecycleScript(launchableId, lifecycleScriptId, signal),
    enabled: Boolean(launchableId && lifecycleScriptId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  };
}

export function useLaunchableLifecycleScriptQuery(
  launchableId: string,
  lifecycleScriptId: string,
) {
  return useQuery(
    launchableLifecycleScriptQueryOptions(launchableId, lifecycleScriptId),
  );
}
